import React, { Fragment, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
    ChevronUpIcon,
    ChevronDownIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon,
    CheckCircleIcon,
    ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { LoadingContainer } from './ui/Spinner';
import { formatCurrency, formatDuration, formatDate } from '../utils/formatting';
import SessionForm from './SessionForm';

function SessionList({ onUpdate, onOpenModal, className = '', sessions: propSessions, loading: propLoading, error: propError, onLoadMore, hasMore, noPaper, hideAddButton }) {
    const [internalSessions, setInternalSessions] = useState([]);
    const [showImport, setShowImport] = useState(false);
    const [deleteSessionId, setDeleteSessionId] = useState(null);
    const [editingSession, setEditingSession] = useState(null);
    const [internalLoading, setInternalLoading] = useState(true);
    const [internalError, setInternalError] = useState(null);

    // Use provided sessions if available, otherwise use internal state
    const sessions = propSessions || internalSessions;
    const loading = propLoading !== undefined ? propLoading : internalLoading;
    const error = propError || internalError;

    useEffect(() => {
        // Only fetch sessions if they're not provided as props
        if (propSessions === undefined) {
            const fetchSessions = async () => {
                setInternalLoading(true);
                setInternalError(null);
                try {
                    const response = await axios.get('/api/sessions/', {
                        params: { 
                            ordering: '-start_time'  // Order by start time descending
                        }
                    });
                    // Get all sessions and take the first 10 (since we're ordering by -start_time)
                    // Handle both paginated response (with results) and direct array
                    const allSessions = Array.isArray(response.data)
                        ? response.data
                        : (response.data.results || []);
                    setInternalSessions(allSessions.slice(0, 10));
                } catch (error) {
                    console.error('Failed to fetch sessions:', error);
                    setInternalError('Failed to load sessions');
                } finally {
                    setInternalLoading(false);
                }
            };
            fetchSessions();
        }
    }, [propSessions]);



    const handleDelete = async () => {
        if (!deleteSessionId) return;
        try {
            await axios.delete(`/api/sessions/${deleteSessionId}/`);
            // If sessions are provided externally, just call onUpdate
            if (propSessions !== undefined) {
                if (onUpdate) onUpdate();
            } else {
                // Otherwise, update the internal sessions list
                setInternalSessions(prevSessions => prevSessions.filter(s => s.id !== deleteSessionId));
            }
            setDeleteSessionId(null);
        } catch (error) {
            console.error('Error deleting session:', error);
        }
    };

    const handleEditSuccess = () => {
        setEditingSession(null);
        // If sessions are provided externally, just call onUpdate
        if (propSessions !== undefined) {
            if (onUpdate) onUpdate();
            return;
        }
        // Otherwise, refresh the internal sessions list
        const fetchSessions = async () => {
            try {
                const response = await axios.get('/api/sessions/', {
                    params: { 
                        ordering: '-start_time'  // Order by start time descending
                    }
                });
                // Get all sessions and take the first 10 (since we're ordering by -start_time)
                const allSessions = response.data;
                setInternalSessions(allSessions.slice(0, 10));
            } catch (error) {
                console.error('Failed to fetch sessions:', error);
            }
        };
        fetchSessions();
    };

    if (loading) {
        return (
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                         rounded-lg shadow-sm ${className}`}>
                <LoadingContainer />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg shadow-sm ${className}`}>

                <div className="p-4">
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                      rounded-lg shadow-sm ${className}`}>


            <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm text-left text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 dark:text-gray-300 
                                   bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th scope="col" className="px-4 py-3">Location</th>
                            <th scope="col" className="px-4 py-3">Date</th>
                            <th scope="col" className="px-4 py-3">Duration</th>
                            <th scope="col" className="px-4 py-3">Stake</th>
                            <th scope="col" className="px-4 py-3">
                                <div className="flex items-center">
                                    Profit
                                    <div className="flex flex-col ml-1">
                                        <ChevronUpIcon className="h-3 w-3" />
                                        <ChevronDownIcon className="h-3 w-3" />
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className="px-4 py-3">Rakeback</th>
                            <th scope="col" className="px-4 py-3">Grades</th>
                            <th scope="col" className="px-4 py-3">Mistakes</th>
                            <th scope="col" className="px-4 py-3">Hands</th>
                            <th scope="col" className="px-4 py-3">Big Folds</th>
                            <th scope="col" className="px-4 py-3">Curiosity Calls</th>
                            <th scope="col" className="px-4 py-3">Review</th>
                            <th scope="col" className="px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(sessions) && sessions.map((session) => (
                            <tr key={session.id} 
                                className={`border-b border-gray-200 dark:border-gray-700 
                                          bg-white dark:bg-gray-800 
                                          hover:bg-gray-50 dark:hover:bg-gray-700`}>
                                <td className="p-4 font-medium text-gray-900 dark:text-white">
                                    {session.location?.name || 'N/A'}
                                </td>
                                <td className="p-4 text-gray-600 dark:text-gray-300">
                                    {formatDate(session.start_time)}
                                </td>
                                <td className="p-4 text-gray-600 dark:text-gray-300">
                                    {formatDuration(session.duration)}
                                </td>
                                <td className="p-4 text-gray-600 dark:text-gray-300">
                                    {session.stake?.name || 'N/A'}
                                </td>
                                <td className="p-4">
                                    <span className={`font-medium ${
                                        session.profit > 0 
                                            ? 'text-green-600 dark:text-green-400' 
                                            : session.profit < 0 
                                                ? 'text-red-600 dark:text-red-400'
                                                : 'text-gray-600 dark:text-gray-400'
                                    }`}>
                                        {formatCurrency(session.profit)}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <span className="font-medium text-green-600 dark:text-green-400">
                                        {formatCurrency(session.rakeback || 0)}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <div className="flex gap-1">
                                        {session.technical_grade && (
                                            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded ${
                                                session.technical_grade === 'A' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                                session.technical_grade === 'B' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                                                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                            }`} title="Technical Grade">
                                                {session.technical_grade}
                                            </span>
                                        )}
                                        {session.mental_grade && (
                                            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded ${
                                                session.mental_grade === 'A' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                                session.mental_grade === 'B' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                                                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                            }`} title="Mental Grade">
                                                {session.mental_grade}
                                            </span>
                                        )}
                                        {!session.technical_grade && !session.mental_grade && (
                                            <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                                        )}
                                    </div>
                                </td>
                                <td className="p-4 text-center text-gray-600 dark:text-gray-300">
                                    {session.mistakes_count > 0 ? (
                                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                            {session.mistakes_count}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                                <td className="p-4 text-center text-gray-600 dark:text-gray-300">
                                    {session.big_hands_count > 0 ? (
                                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                            {session.big_hands_count}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                                <td className="p-4 text-center text-gray-600 dark:text-gray-300">
                                    {session.big_folds_count > 0 ? (
                                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                            {session.big_folds_count}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                                <td className="p-4 text-center text-gray-600 dark:text-gray-300">
                                    {session.curiosity_calls_count > 0 ? (
                                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                                            {session.curiosity_calls_count}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                                <td className="p-4 text-center">
                                    {session.status === 'completed' ? (
                                        session.reviewed ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" title="Session reviewed">
                                                <CheckCircleIcon className="h-3.5 w-3.5" />
                                                Done
                                            </span>
                                        ) : (
                                            <Link
                                                to={`/sessions/${session.id}/review`}
                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                                                title={`${session.unreviewed_hands_count || 0} hands to review`}
                                            >
                                                <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                                                {session.unreviewed_hands_count > 0 ? session.unreviewed_hands_count : 'Review'}
                                            </Link>
                                        )
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    <div className="flex gap-2">
                                        <Link
                                            to={`/sessions/${session.id}`}
                                            className="p-1 text-gray-600 hover:text-gray-900 
                                                     dark:text-gray-400 dark:hover:text-white
                                                     transition-colors duration-200"
                                        >
                                            <EyeIcon className="h-5 w-5" />
                                        </Link>
                                        <button
                                            onClick={() => setEditingSession(session)}
                                            aria-label="Edit session"
                                            className="p-1 text-gray-600 hover:text-gray-900
                                                     dark:text-gray-400 dark:hover:text-white
                                                     transition-colors duration-200"
                                        >
                                            <PencilIcon className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteSessionId(session.id)}
                                            aria-label="Delete session"
                                            className="p-1 text-gray-600 hover:text-red-600
                                                     dark:text-gray-400 dark:hover:text-red-400
                                                     transition-colors duration-200"
                                        >
                                            <TrashIcon className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {Array.isArray(sessions) && sessions.length === 0 && (
                            <tr>
                                <td colSpan="13" className="p-4 text-center text-gray-600 dark:text-gray-400">
                                    No sessions found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>



            {/* Delete Confirmation Modal */}
            <Transition appear show={!!deleteSessionId} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setDeleteSessionId(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden 
                                                       rounded-2xl bg-white dark:bg-gray-800 
                                                       border border-gray-200 dark:border-gray-700
                                                       p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                        Delete Session
                                    </Dialog.Title>
                                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                                        Are you sure you want to delete this session? This action cannot be undone.
                                    </p>
                                    <div className="flex justify-end gap-3">
                                        <button
                                            onClick={() => setDeleteSessionId(null)}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 
                                                     bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                                                     hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            className="px-4 py-2 text-sm font-medium text-white 
                                                     bg-red-600 hover:bg-red-700 rounded-lg 
                                                     transition-colors"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Import Modal */}
            <Transition appear show={showImport} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setShowImport(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden 
                                                       rounded-2xl bg-white dark:bg-gray-800 
                                                       border border-gray-200 dark:border-gray-700
                                                       p-6 text-left align-middle shadow-xl transition-all">
                                                            <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Import Sessions
                        </Dialog.Title>
                                    {/* Import form will go here */}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Edit Session Modal */}
            <Transition appear show={!!editingSession} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setEditingSession(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden 
                                                       rounded-2xl bg-white dark:bg-gray-800 
                                                       border border-gray-200 dark:border-gray-700
                                                       p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex justify-between items-center">
                                        Edit Session
                                        <button
                                            onClick={() => setEditingSession(null)}
                                            aria-label="Close"
                                            className="p-1 text-gray-600 hover:text-gray-900
                                                     dark:text-gray-400 dark:hover:text-white
                                                     transition-colors duration-200"
                                        >
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </Dialog.Title>
                                    <SessionForm session={editingSession} onSuccess={handleEditSuccess} />
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}

export default SessionList;